import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import saveAs from 'file-saver';
import get from 'lodash.get';
import moment from 'moment';
import Papa from 'papaparse';
import {
  Backdrop,
  Box,
  Card,
  Chip,
  DotProgress,
  FormDialog,
  Grid,
  Icon,
  IconButton,
  MuiBox,
  MuiTable,
  SvgIcon,
  Tooltip,
  Typography,
  color
} from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { selectors as activityLogSelectors, actions as activityLogActions, BaseEvent } from 'store/activityLog';
import { selectors as adminsSelectors, actions as adminsActions } from 'store/admins';
import { selectors as clientsSelectors, Client } from 'store/clients';
import { selectors as kioskSelectors, actions as kioskActions } from 'store/kiosks';
import { useStyles } from './styled';
import { FiltersOverlay } from './components/FiltersOverlay';

// FORMATS THE ACTIVITYLOG OBJECT FOR EACH COLUMN
const formatActivityLog = (activityLog: BaseEvent[]) => {
  return activityLog.map((r) => ({
    date: r.createdAt ? format(new Date(r.createdAt), 'MM/dd/yyyy hh:mm a') : '',
    user: get(r.actorInfo, 'name'),
    kioskName: r.name,
    action: r.event
  }));
};

const ActivityLogPage: React.FC = () => {
  const classes = useStyles()();
  // ACTIONS AND SELECTORS
  const { getActivityLog, resetActivityLog, resetExportData, getAdmins, getKiosks } = useActions({
    getActivityLog: activityLogActions.getActivityLog,
    resetActivityLog: activityLogActions.resetActivityLog,
    resetExportData: activityLogActions.resetExportData,
    getAdmins: adminsActions.getAdmins,
    getKiosks: kioskActions.getKiosks
  });
  const displayedActivityLog = useSelector(activityLogSelectors.selectActivityLog);
  const activityLogPending = useSelector(activityLogSelectors.selectPending);
  const hasMore = useSelector(activityLogSelectors.selectHasMore);
  const totalRecords = useSelector(activityLogSelectors.selectTotalRecords);
  const exportData = useSelector(activityLogSelectors.selectExportData);
  const exportPending = useSelector(activityLogSelectors.selectExportPending);
  const admins = useSelector(adminsSelectors.selectAdmins);
  const client = useSelector(clientsSelectors.selectClient) as Client;
  const kioskData = useSelector(kioskSelectors.selectKiosks);

  const [showFiltersDrawer, setShowFiltersDrawer] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [isLoading, setIsLoading] = React.useState(false);
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const [sortColumn, setSortColumn] = React.useState('created_at');
  const [sortDirection, setSortDirection] = React.useState('desc');
  const [sortParams, setSortParams] = React.useState({ column: 'created_at', order: 'desc' });
  const [kioskIdsToFilter, setKioskIdsToFilter] = React.useState<string[]>([]);
  const [usersToFilter, setUsersToFilter] = React.useState<string[]>([]);
  const [selectedEndDate, setSelectedEndDate] = React.useState<Date | null>(null);
  const [selectedStartDate, setSelectedStartDate] = React.useState<Date | null>(null);
  const [filterChips, setFilterChips] = React.useState<string[]>([]);
  const [chipClosed, setChipClosed] = React.useState(false);
  const [filter, setFilter] = React.useState(false);

  // FORMATS ACTIVITYLOG TO FIT HOW THE TABLE SHOULD DISPLAY THE DATA
  const formattedActivityLog = useMemo(() => formatActivityLog(displayedActivityLog), [displayedActivityLog]);

  // ON FETCH: SHOW OVERLAY IF NO DATA
  React.useEffect(() => {
    if (displayedActivityLog.length <= 0 && !activityLogPending) {
      setShowConfirmation(true);
    } else {
      setShowConfirmation(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedActivityLog, activityLogPending]);

  // ON FILTER REMOVAL: FETCH
  React.useEffect(() => {
    if (chipClosed) {
      handleFilter();
      setChipClosed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStartDate, selectedEndDate, kioskIdsToFilter, usersToFilter]);

  // ON UPDATE OF CLIENT || SUCCESSFUL: RESET
  React.useEffect(() => {
    // RESET ACTIVITY LOG ON CLIENT CHANGE
    resetActivityLog();
    resetFilters();
    setPage(1);
    // IF CLIENT EXIST OR SUCCESSFUL ACTION - GET ACTIVITIES
    if (client) {
      getKiosks({});
      getAdmins();
      getActivityLog({ page: 1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  // ON PAGE CHANGE: FETCH MORE DATA
  React.useEffect(() => {
    if (page > 1) {
      let payload = {
        page,
        sort: sortDirection,
        sortColumn
      };
      if (filter) {
        const appliedFilters = getAppliedFilters();
        payload = { ...payload, ...appliedFilters };
      }
      getActivityLog(payload);
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // ON EXPORT DATA FETCHED: WRITE TO CSV
  React.useEffect(() => {
    if (exportData && exportData.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const formattedData = formatActivityLog(exportData).map((item: any) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const convertedItem: any = {};
        Object.entries(item).forEach(([key, value]) => {
          const upperCaseKey = key.toUpperCase();
          convertedItem[upperCaseKey] = value;
        });
        return convertedItem as BaseEvent;
      });

      const csvData = Papa.unparse(formattedData);
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8' });
      saveAs(blob, 'Activity Log.csv');

      resetExportData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  // INCREMENT PAGE TO TRIGGER NEXT FETCH
  const loadMore = () => {
    const activityLogTable: HTMLElement = document.getElementById('activityLogTable') || new HTMLElement();
    if (
      activityLogTable.offsetHeight < activityLogTable.scrollHeight &&
      activityLogTable.offsetHeight + activityLogTable.scrollTop >= activityLogTable.scrollHeight - 1
    ) {
      if (hasMore && !activityLogPending && !isLoading) {
        setIsLoading(true);
        setPage((prevPage) => prevPage + 1);
      }
    }
  };

  // RETURN ALL ACTIVE FILTERS FOR FETCH PAYLOAD
  const getAppliedFilters = () => {
    return {
      kioskIds: kioskIdsToFilter,
      startTime: selectedStartDate && moment(selectedStartDate).utc().format(),
      endTime: selectedEndDate && moment(selectedEndDate).utc().format(),
      userNames: usersToFilter
    };
  };

  // EXPORT TO CSV BUTTON CLICK
  const onExportButtonClick = () => {
    let payload = {
      page: 1,
      sort: sortDirection,
      sortColumn,
      exportData: true
    };
    if (filter) {
      const appliedFilters = getAppliedFilters();
      payload = { ...payload, ...appliedFilters };
    }
    getActivityLog(payload);
  };

  // SET THE SORT COLUMN AND DIRECTION
  const handleSort = (column: string) => {
    let newSortDirection = 'asc';
    if (column === sortColumn) {
      newSortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      setSortColumn(column);
    }
    setSortDirection(newSortDirection);
    handleHeaderClick(column);
  };

  // SORT BY SELECTED HEADER AND FETCH
  const handleHeaderClick = (key: string) => {
    const sortOrder = sortParams.column === key && sortParams.order === 'asc' ? 'desc' : 'asc';
    setSortParams({ column: key, order: sortOrder });
    setPage(1);
    let payload = { sortColumn: key, sort: sortOrder, page: 1 };
    if (filter) {
      const appliedFilters = getAppliedFilters();
      payload = { ...payload, ...appliedFilters };
    }
    getActivityLog(payload);
  };

  // APPLY FILTER(S) AND FETCH
  const handleFilter = () => {
    setFilter(true);
    setPage(1);
    const appliedFilters = getAppliedFilters();
    getActivityLog({
      ...appliedFilters,
      page: 1,
      sort: sortDirection,
      sortColumn
    });
  };

  // CLEAR ALL ACTIVE FILTERS
  const resetFilters = () => {
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setKioskIdsToFilter([]);
    setUsersToFilter([]);
    setFilterChips([]);
  };

  // REMOVE AN ACTIVE FILTER
  const handleChipClose = (val: string) => {
    if (val === 'Kiosk') {
      setKioskIdsToFilter([]);
    } else if (val === 'User') {
      setUsersToFilter([]);
    } else if (val === 'Start Date Time') {
      setSelectedStartDate(null);
    } else if (val === 'End Date Time') {
      setSelectedEndDate(null);
    }

    const newChips = filterChips && filterChips.filter((chip: string) => chip !== val);
    setFilterChips(newChips);
  };

  const SortArrow = () => {
    return (
      <Box sx={{ display: 'inline-block', cursor: 'pointer' }}>
        <SvgIcon name={sortDirection === 'asc' ? 'drop_up' : 'drop_down'} size={15} style={{ marginLeft: '5px' }} />
      </Box>
    );
  };

  return (
    <MuiBox display="flex" flexDirection="column" height="100%" paddingBottom="24px">
      {(activityLogPending || isLoading) && formattedActivityLog.length <= 0 && (
        <Backdrop open>
          <DotProgress color={color.LIGHT_GRAY} />
        </Backdrop>
      )}

      {showConfirmation && !activityLogPending && (
        <FormDialog
          cancelButtonAction={() => setShowConfirmation(false)}
          confirmButtonAction={() => setShowConfirmation(false)}
          confirmButtonText="Ok"
          open={showConfirmation}
        >
          <Typography fontSize={24} maxWidth="524px">
            There is no data to display.
          </Typography>
        </FormDialog>
      )}

      {/* HEADER ROW */}
      <Grid className={classes.activitylogHeading}>
        <Box flexOverride="none">
          <Typography className={classes.activitylogText}>Activity Log</Typography>
        </Box>
      </Grid>

      <Grid
        className={classes.activitylogOptions}
        style={{ paddingBottom: filterChips && filterChips.length === 0 ? '24px' : '0px' }}
      >
        <Tooltip title="Filters" placement="top" arrow verticalOffset={-8}>
          <IconButton
            onClick={() => setShowFiltersDrawer(true)}
            data-testid="filter-button"
            style={{
              backgroundColor: '#F46D2E',
              color: 'white'
            }}
          >
            <Icon name="FilterAltOutlined" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Export to .csv" placement="top" arrow verticalOffset={-8}>
          <IconButton
            onClick={() => onExportButtonClick()}
            disabled={formatActivityLog.length <= 0}
            style={{
              backgroundColor: '#F46D2E',
              color: 'white'
            }}
            data-testid="filter-button"
          >
            {!exportPending ? (
              <Icon name="FileDownloadOutlined" />
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <DotProgress size={3} />
              </div>
            )}
          </IconButton>
        </Tooltip>
      </Grid>
      {filterChips && filterChips.length > 0 && (
        <Grid className={classes.filterOptions}>
          {filterChips.map((chip: string, index: number) => (
            <Chip
              key={`${index}-${chip}`}
              sx={{
                backgroundColor: '#F46D2E',
                color: 'white',
                fontWeight: 900,
                marginRight: '8px'
              }}
              className={classes.buttonStyl}
              label={chip}
              onDelete={() => {
                handleChipClose(chip);
                setChipClosed(true);
              }}
              variant="outlined"
            />
          ))}
        </Grid>
      )}

      {/* TABLE OF USERS */}
      <MuiBox className={classes.activitylogOuterBox}>
        <Card hasBorderRadius raised fullHeight sx={{ flex: '1 0 0', display: 'flex', flexDirection: 'column' }}>
          <MuiTable.TableContainer
            onScroll={() => loadMore()}
            id="activityLogTable"
            sx={{ flex: 1, overflowY: 'auto' }}
          >
            <MuiTable.Table stickyHeader sx={{ borderCollapse: 'collapse' }}>
              <MuiTable.TableHead>
                <MuiTable.TableRow>
                  <MuiTable.TableCell
                    className={classes.activitylogHeaders}
                    sx={{ fontWeight: 600 }}
                    onClick={() => handleSort('created_at')}
                  >
                    Date
                    {sortColumn === 'created_at' && <SortArrow />}
                  </MuiTable.TableCell>
                  <MuiTable.TableCell
                    className={classes.activitylogHeaders}
                    sx={{ fontWeight: 600, width: '200px' }}
                    onClick={() => handleSort('actor_info')}
                  >
                    User
                    {sortColumn === 'actor_info' && <SortArrow />}
                  </MuiTable.TableCell>
                  <MuiTable.TableCell
                    className={classes.activitylogHeaders}
                    sx={{ fontWeight: 600, width: '180px' }}
                    onClick={() => handleSort('name')}
                  >
                    Kiosk
                    {sortColumn === 'name' && <SortArrow />}
                  </MuiTable.TableCell>
                  <MuiTable.TableCell
                    className={classes.activitylogHeaders}
                    sx={{ fontWeight: 600 }}
                    onClick={() => handleSort('event')}
                  >
                    Action
                    {sortColumn === 'event' && <SortArrow />}
                  </MuiTable.TableCell>
                  <MuiTable.TableCell />
                </MuiTable.TableRow>
              </MuiTable.TableHead>
              <MuiTable.TableBody>
                {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  formattedActivityLog.map((activitylogObj: any) => {
                    return (
                      <MuiTable.TableRow className={classes.activitylogTableRows}>
                        <MuiTable.TableCell>
                          <Typography fontSize={16} fontWeight={600}>
                            {activitylogObj.date}
                          </Typography>
                        </MuiTable.TableCell>
                        <MuiTable.TableCell>
                          <Typography fontSize={16}>{activitylogObj.user}</Typography>
                        </MuiTable.TableCell>
                        <MuiTable.TableCell>
                          <Typography fontSize={16}>{activitylogObj.kioskName}</Typography>
                        </MuiTable.TableCell>
                        <MuiTable.TableCell>
                          <Typography>{activitylogObj.action}</Typography>
                        </MuiTable.TableCell>
                      </MuiTable.TableRow>
                    );
                  })
                }
              </MuiTable.TableBody>
            </MuiTable.Table>
          </MuiTable.TableContainer>
        </Card>
      </MuiBox>
      <Grid className={classes.footer}>
        <p className={classes.total}>Actions: {totalRecords}</p>
      </Grid>
      <FiltersOverlay
        isOpen={showFiltersDrawer}
        onClose={() => setShowFiltersDrawer(false)}
        kioskIdsToFilter={kioskIdsToFilter}
        setKioskIdsToFilter={setKioskIdsToFilter}
        usersToFilter={usersToFilter}
        setUsersToFilter={setUsersToFilter}
        selectedStartDate={selectedStartDate}
        setSelectedStartDate={setSelectedStartDate}
        selectedEndDate={selectedEndDate}
        setSelectedEndDate={setSelectedEndDate}
        filterChips={filterChips}
        setFilterChips={setFilterChips}
        kioskList={kioskData}
        admins={admins}
        handleFilter={handleFilter}
      />
    </MuiBox>
  );
};

export default ActivityLogPage;
